import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  getLiturgicalSeasonForDate,
  SEASON_COLORS,
  SEASON_OPTIONS,
  DEFAULT_LITURGICAL_COLOR,
} from './utils/liturgical';
import { useUserProfile } from './UserProfileContext';

// Holds today's liturgical season for the whole app so the root element can
// theme off it. romcal computes this locally from the date, so there is no
// fetch here and nothing to fail over the network — if resolution throws, the
// season is null, data-season is omitted, and :root's indigo holds.

const LiturgicalContext = createContext({
  season: null,
  color: DEFAULT_LITURGICAL_COLOR,
  accent: null,
  name: null,
});

export function useLiturgical() {
  return useContext(LiturgicalContext);
}

export function LiturgicalProvider({ children }) {
  const [today, setToday] = useState(() => new Date());

  // Manual season override from the user's profile. Null/empty means rotate
  // with the calendar. UserProfileProvider wraps this provider in App(), so
  // prefs are available here. Prefs load async, so the first render always
  // takes the auto path and the override applies a frame later — accepted.
  const { prefs } = useUserProfile();
  const themeOverride = prefs.themeOverride || null;

  // Roll the theme over at local midnight so a long-lived tab doesn't sit on
  // yesterday's season.
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5);
    const timer = setTimeout(() => setToday(new Date()), midnight.getTime() - now.getTime());
    return () => clearTimeout(timer);
  }, [today]);

  const value = useMemo(() => {
    // Guard on SEASON_COLORS so a stale or unrecognized value in SharePoint
    // falls back to the calendar rather than theming with an undefined color.
    if (themeOverride && SEASON_COLORS[themeOverride]) {
      const opt = SEASON_OPTIONS.find(o => o.value === themeOverride);
      return {
        season: themeOverride,
        color: SEASON_COLORS[themeOverride],
        accent: null,
        name: opt ? opt.label : themeOverride,
      };
    }
    return getLiturgicalSeasonForDate(today);
  }, [today, themeOverride]);

  return (
    <LiturgicalContext.Provider value={value}>
      {children}
    </LiturgicalContext.Provider>
  );
}
